import { createContext, useContext, useEffect, useState, useRef } from "react";
import { api, setToken, getToken, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

const IDLE_TIMEOUT_S = 5 * 60; // 5 menit

function useIdleTimer(logout) {
  const idleRef = useRef(null);

  const resetIdle = () => {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => {
      logout();
      window.location.href = "/login";
    }, IDLE_TIMEOUT_S * 1000);
  };

  useEffect(() => {
    const events = ["mousedown", "keydown", "touchstart", "mousemove", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => {
      if (idleRef.current) clearTimeout(idleRef.current);
      events.forEach((e) => window.removeEventListener(e, resetIdle));
    };
  }, []); // only mount once
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, obj=in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!getToken()) {
        if (mounted) {
          setUser(false);
          setLoading(false);
        }
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (mounted) setUser(data);
      } catch {
        setToken(null);
        if (mounted) setUser(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    // Merge property info into user object
    const userData = { ...data.user, property: data.property };
    setUser(userData);
    return { user: userData, trial_expired: data.trial_expired };
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken(null);
    setUser(false);
  };

  // Pasang idle timer ketika user sudah login
  const isLoggedIn = !!user && user !== false;
  return (
    <AuthCtx.Provider value={{ user, loading, login, logout }}>
      {isLoggedIn && <IdleWatcher logout={logout} />}
      {children}
    </AuthCtx.Provider>
  );
}

function IdleWatcher({ logout }) {
  const idleRef = useRef(null);

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  const resetIdle = () => {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = setTimeout(handleLogout, IDLE_TIMEOUT_S * 1000);
    // Kirim ping ke backend agar session tetap sync
    api.post("/auth/ping").catch(() => {});
  };

  useEffect(() => {
    const events = ["mousedown", "keydown", "touchstart", "mousemove", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => {
      if (idleRef.current) clearTimeout(idleRef.current);
      events.forEach((e) => window.removeEventListener(e, resetIdle));
    };
  }, []);

  return null;
}

export function useAuth() {
  return useContext(AuthCtx);
}

export { formatApiError };
